<template>
    <modal-side-generic
    sizeClass="big"
    :headerColor="headerColor"
    :applyErrorState="applyErrorState"
    :title="selectedBatchFiles.sourceReference"
    :subTitle="amountFormat(Math.abs(selectedBatchFiles.totalAmount), selectedBatchFiles.currency)"
    :subTitleIcon="headerTitleIcon"
    :icon="headerBigIcon"
    :buttonsTopData="selectedBatchFiles.hasReceipt ? buttonsTopData : null"
    @hideMe="emit('hideMe')">
        <!-- Loader Generic -->
        <loaders-generic v-if="showLoader" fullSize/>
        <!-- Error State -->
		<empty-state-error v-if="applyErrorState && !showLoader" :message="t('genericErrorMessage')"/>
        <div class="batchfile-details" v-if="!showSpinner && !applyErrorState">
            <div
            v-for="(lineGroup, index) in parsedDetail"
            :key="`lineGroup${lineGroup.index}`"
            class="group-wrapper">
                <divider-generic :icon="lineGroup.icon" :title="lineGroup.label" :applyBorder="index !== 0"/>
                <!-- Text Block for each line -->
                <text-block-generic
                v-for="(line, index) in lineGroup.lines"
                applyEllipsis
                :key="`lineGroupLine${line,index}`"
                :params="line"
                :icon="line.icon"
                :colorIconPreset="line.colorIconPreset"
                :mask="line.mask"/>
                <!-- Buttons -->
                <div class="buttons-wrapper" v-if="lineGroup.buttons">
                    <a
                    v-for="(button, index) in lineGroup.buttons"
                    :key="`lineGroupButton${button,index}`"
                    :href="button.downloadUrl"
                    target="_blank"
                    download>
                    <button-tertiary
                    class="button"
                    :label="button.text"
                    :icon="button.icon"
                    @clicked="button.clickFn"/>
                    </a>
                </div>
                <!-- Table -->
                <table-generic
                v-if="lineGroup.table"
                noBoxStyles
                noHoverStyles
                rowHeightClass="small"
                :hasDetail="false"
                :headerData="lineGroup.table.tableHeaderData"
                :tableData="lineGroup.table.tableData"
                :tableStyles="lineGroup.table.tableStyles"
                @moreOptionsClicked="moreOptionsClickHandler" />
            </div>
        </div>
    </modal-side-generic>
    <!-- Feedback -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    @hideMe="showFeedbackToast = false"/>
    <!-- Send Info Modal -->
    <modal-centered-send-info
    v-if="showSendInfoModalEmail || showSendInfoModalSMS"
    emailAction="sendOperationEmailNotification"
    smsAction="sendOperationSMSNotification"
    :sendType="showSendInfoModalEmail ? 'email' : 'sms'"
    :payload="sendInfoPayload"
    @hideMe="[showSendInfoModalEmail = false, showSendInfoModalSMS = false]"/>
    <!-- Component to handle download logic-->
	<download-logic-component :downloadLink="downloadLink" @clearDownloadLink="downloadLink = null"/>
    <!-- Share Link Modal -->
    <modal-centered-share-link
    v-if="showShareLink"
    :operationId="shareLinkModalData.operationId"
    :payload="shareLinkModalData.payload"
    @hideMe="showShareLink = false"/>
</template>

<script setup>
import ModalSideGeneric from '@/components/generic/modals/side/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateError from '@/components/generic/emptyStates/Error'
import TableGeneric from '@/components/generic/table/Generic'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'
import DownloadLogicComponent from '@/components/generic/DownloadLogicComponent'
import ModalCenteredSendInfo from '@/components/generic/modals/centered/sendInfo/Base'
import ModalCenteredShareLink from '@/components/generic/modals/centered/ShareLink'

/* Vue & Helpers */
import { ref, computed } from 'vue'
import { amountFormat } from '@/assets/scripts/amounts'
import { getDateWithTime } from '@/assets/scripts/dateBuilders'
import { ACCOUNT_NUMBER } from '@/assets/constants/masks'
import { iconMapper, colorStatusMapperModal, iconColorMapper } from '@/assets/scripts/mappers/batchFiles'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { createDownloadGenericReceiptPdf } from '@/network/api/operations/operations.api.js'
import { createBatchfilesExcelURL, createBatchfilesPDFReceipt } from '@/network/api/cardtopup.api.js'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const { requestBatchFileDetail } = mapActions(moduleMapper.CARDS_BATCHFILE_CONSULT)

/* Emmitters */
const emit = defineEmits(['hideMe'])

const props = defineProps({
    selectedBatchFiles: Array
})

/* Data */
const showSpinner = ref(false)
const detailInfo = ref(null)
const buttonsTopData = ref({})
const showSendInfoModalEmail = ref(false)
const showSendInfoModalSMS = ref(false)
const sendInfoPayload = ref({})
const downloadLink = ref(null)
const showShareLink = ref(false)
const shareLinkModalData = ref({})

/*************************************************
*                                                *
*                Side Modal Info                 *
*                                                *
*************************************************/
const headerColor = computed(() => {
    return colorStatusMapperModal(props.selectedBatchFiles.status)
})

const headerBigIcon = computed(() => {
    return iconMapper(props.selectedBatchFiles.status)
})

const headerTitleIcon = computed(() => {
    if (props.selectedBatchFiles.totalAmount === 0) return null
    return props.selectedBatchFiles.totalAmount > 0 ? 'arrow-down-full' : 'arrow-up-full'
})

buttonsTopData.value = {
	downloadUrl: createDownloadGenericReceiptPdf(({ params: { operationID: props.selectedBatchFiles.customerOperationId } })),
    shareLinkPayload: 'generateShareLink',
    emailAction: 'sendOperationEmailNotification',
    smsAction: 'sendOperationSMSNotification',
    sendPayload: { operationId: props.selectedBatchFiles.customerOperationId },
    operationId: props.selectedBatchFiles.customerOperationId
}

/*************************************************
*                                                *
*               Batch Files Detail               *
*                                                *
*************************************************/
const parsedDetail = computed(() => {
    return [
        {
            label: t('cards.batchfile.consult.detailModal.originAccount.title'),
            icon: 'account-balance',
            lines: [{
                label: t('cards.batchfile.consult.detailModal.originAccount.accountLabel'),
                content: detailInfo.value.originAccount.accountNumber || '-',
                mask: ACCOUNT_NUMBER
            }]
        },
        {
            label: t('cards.batchfile.consult.detailModal.email.title'),
            icon: 'letter',
            lines: [{
                label: t('cards.batchfile.consult.detailModal.email.emailLabel'),
                content: detailInfo.value.email || '-'
            }]
        },
        {
            label: t('cards.batchfile.consult.detailModal.fileInfo.title'),
            icon: 'info',
            lines: [{
                label: t('cards.batchfile.consult.detailModal.fileInfo.fileDescriptionLabel'),
                content: detailInfo.value.sourceReference || '-'
            }, {
                label: t('cards.batchfile.consult.detailModal.fileInfo.totalAmountLabel'),
                content: amountFormat(Math.abs(detailInfo.value.totalAmount), detailInfo.value.currency) || '-',
                icon: 'arrow-down-full',
                colorIconPreset: 'negative',
                classes: 'amount-icon'
            }, {
                label: t('cards.batchfile.consult.detailModal.fileInfo.totalEntriesLabel'),
                content: detailInfo.value.totalEntries || '-'
            }, {
                label: t('cards.batchfile.consult.detailModal.fileInfo.submissionDateLabel'),
                content: getDateWithTime(detailInfo.value.submitDate) || '-',
                classes: 'capitalize'
            }, {
                label: t('cards.batchfile.consult.detailModal.fileInfo.cardTypeLabel'),
                content: detailInfo.value.cardGroup.name || '-'
            }, {
                label: t('cards.batchfile.consult.detailModal.fileInfo.statusLabel'),
                content: detailInfo.value.statusDescription || '-',
                icon: iconMapper(detailInfo.value.status),
                colorIconPreset: iconColorMapper(detailInfo.value.status)
            }],
            buttons: [{
                icon: 'download',
                text: t('cards.batchfile.consult.detailModal.fileInfo.downloadButtonText'),
                downloadUrl: createBatchfilesExcelURL({ batchfileId: props.selectedBatchFiles.id })
            }
            // This button is hidden on purpose. Waiting for correct API
            // {
            //     icon: 'email',
            //     text: t('cards.batchfile.consult.detailModal.fileInfo.emailButtonText'),
            //     clickFn: () => alert('clicked')
            // }
            ]
        },
        {
            label: t('cards.batchfile.consult.detailModal.charges.title'),
            icon: 'card-recharge',
            table: {
                tableHeaderData: [
                    { label: t('cards.batchfile.consult.detailModal.charges.tableHeaders.card'), dataAttr: 'cardNumber', rowCellType: 'title' },
                    { label: t('cards.batchfile.consult.detailModal.charges.tableHeaders.amount'), dataAttr: 'amount', rowCellType: 'amount' },
                    { label: t('cards.batchfile.consult.detailModal.charges.tableHeaders.description'), dataAttr: 'fileDescription' },
                    { label: t('cards.batchfile.consult.detailModal.charges.tableHeaders.status'), dataAttr: 'fileStatus', rowCellType: 'status' },
                    { dataAttr: 'tableOptions', rowCellType: 'more-options' }
                ],
                tableStyles: [
                    { width: '30%' },
                    { width: '15%', 'text-align': 'end' },
                    { 'text-align': 'end' },
                    { width: '18%', 'padding-left': '15px' },
                    { width: '15%', 'padding-left': '15px' }
                ],
                tableData: detailInfo.value.fileEntries.map(entry => {
                    return [
                        { value: entry.idSequenceNumber, dataAttr: 'id' },
                        { type: 'title', value: entry.card.cardNumber, dataAttr: 'cardNumber' },
                        { type: 'amount', value: { content: entry.amount, currency: entry.currency, noBalanceIcon: true }, dataAttr: 'amount' },
                        { value: entry.description || '-', dataAttr: 'fileDescription' },
                        { type: 'status', value: { text: entry.statusDescription || '-', icon: iconMapper(entry.status), classes: iconColorMapper(entry.status) }, dataAttr: 'fileStatus' },
                        { type: 'more-options', value: moreOptions, dataAttr: 'tableOptions', hidden: entry.status !== 'SUCCESS' }
                    ]
                })
            }
        }
    ]
})

const moreOptionsClickHandler = ev => {
    const sequenceNumber = ev.rowId
	moreOptions.find(obj => obj.id === ev.optionClicked).callbackFunction(props.selectedBatchFiles.customerOperationId, props.selectedBatchFiles.id, sequenceNumber)
}

const sendEmail = (customerOperationId, batchFileId, sequence) => {
    sendInfoPayload.value = {
        operationId: customerOperationId,
        data: { sequence: sequence }
    }
    showSendInfoModalEmail.value = true
}

const sendSMS = (customerOperationId, batchFileId, sequence) => {
    sendInfoPayload.value = {
        operationId: customerOperationId,
        data: { sequence: sequence }
    }
    showSendInfoModalSMS.value = true
}

const downloadPDF = (customerOperationId, batchFileId, sequence) => {
    downloadLink.value = createBatchfilesPDFReceipt({ batchfileId: batchFileId, sequence: sequence })
}

const shareLink = (customerOperationId, batchFileId, sequence) => {
    shareLinkModalData.value = {
        operationId: customerOperationId,
        payload: {
            dataParams: { sequence: sequence }
        }
    }
    showShareLink.value = true
}

const moreOptions = [
	{
        id: 1,
		text: t('cards.batchfile.consult.detailModal.charges.moreOptions.download'),
		icon: 'download',
		callbackFunction: downloadPDF
    }, {
		id: 2,
		text: t('cards.batchfile.consult.detailModal.charges.moreOptions.email'),
		icon: 'letter',
		callbackFunction: sendEmail
	}, {
		id: 3,
		text: t('cards.batchfile.consult.detailModal.charges.moreOptions.sms'),
		icon: 'chat',
		callbackFunction: sendSMS
	}, {
		id: 4,
		text: t('cards.batchfile.consult.detailModal.charges.moreOptions.generateLink'),
		icon: 'link',
		callbackFunction: shareLink
	}
]

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const requestBatchfileDetails = async () => {
	showSpinner.value = true
	showFeedbackToast.value = false
    applyErrorState.value = false

    const payload = {
        batchfileId: props.selectedBatchFiles.id
    }

	try {
        const response = await requestBatchFileDetail(payload)
        detailInfo.value = response.data.data
        showSpinner.value = false
	} catch (error) {
		if (error.toString() === 'Cancel') return
		showSpinner.value = false
		errorHandler(error.response.data, requestBatchfileDetails)
	}
}

/*************************************************
*                                                *
*                 Error Feedback                 *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const applyErrorState = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
	showFeedbackToast.value = true
    applyErrorState.value = true

    feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

requestBatchfileDetails()
</script>
